import { el } from '../core/dom.js';

export interface UndoToastOptions {
  /** Message shown before the Undo button. */
  message: string;
  /** Called when the user taps Undo. */
  onUndo: () => void | Promise<void>;
  /** Optional secondary action (e.g. "Add details"). */
  secondary?: { label: string; onClick: () => void | Promise<void> };
  /** Auto-dismiss after this many ms. Defaults to 6000. */
  durationMs?: number;
}

let active: HTMLElement | null = null;
let activeTimer: number | null = null;

function dismiss(): void {
  if (activeTimer !== null) { window.clearTimeout(activeTimer); activeTimer = null; }
  if (active) { active.remove(); active = null; }
}

/**
 * Show a non-blocking "Done. Undo?" toast. Replaces any previous undo toast.
 * Accessibility: uses role=status with aria-live=polite so screen readers read
 * the message once; the Undo button is focusable and not in a focus trap.
 */
export function showUndoToast(opts: UndoToastOptions): void {
  dismiss();
  const node = el('div', {
    class: 'undo-toast',
    attrs: { role: 'status', 'aria-live': 'polite' },
  });
  node.appendChild(el('span', { class: 'undo-toast__msg' }, opts.message));
  const actions = el('div', { class: 'undo-toast__actions' });
  if (opts.secondary) {
    actions.appendChild(el('button', {
      class: 'btn ghost small',
      attrs: { type: 'button' },
      onclick: async () => { dismiss(); await opts.secondary!.onClick(); },
    }, opts.secondary.label));
  }
  actions.appendChild(el('button', {
    class: 'btn small',
    attrs: { type: 'button' },
    onclick: async () => { dismiss(); await opts.onUndo(); },
  }, 'Undo'));
  node.appendChild(actions);
  document.body.appendChild(node);
  active = node;
  activeTimer = window.setTimeout(dismiss, opts.durationMs ?? 6000);
}

export function dismissUndoToast(): void { dismiss(); }
